import _ from 'lodash';
import WixEvent from '../events/WixEvent';

/**
 * @callback $w.MouseEventHandler
 * @summary Handles events fired when the mouse is clicked.
 * @param {$w.MouseEvent} event The mouse event that occurred.
 * @param {$w.$w} $w Deprecated: A selector function. The $w function was used to
 *  enable event handlers to work with elements in [repeaters]($w.Repeater.html). Now, to get
 *  a scoped selector for working with repeater items, use the [$w.at()]($w.html#at) function
 *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`.
 */

/**
 * @class MouseEvent
 * @summary Event that is fired when the mouse is clicked.
 * @description
 *  The properties of a `MouseEvent` refer to the client, page, and screen.
 *  These terms refer to different containers as shown in the diagrams below.
 *  ![Coodinate Reference](images/coordinates_client.png "Coordinate Reference")
 *  ![Coodinate Reference](images/coordinates_page.png "Coordinate Reference")
 *  ![Coodinate Reference](images/coordinates_screen.png "Coordinate Reference")
 * @memberof $w
 * @mixes $w.Event
 */
class MouseEvent extends WixEvent {
    constructor(target, event) {
        super(target, event);

        this.pageX = _.get(event, 'pageX');
        this.pageY = _.get(event, 'pageY');
        this.screenX = _.get(event, 'screenX');
        this.screenY = _.get(event, 'screenY');
        this.clientX = _.get(event, 'clientX');
        this.clientY = _.get(event, 'clientY');
        this.offsetX = _.get(event, 'nativeEvent.offsetX');
        this.offsetY = _.get(event, 'nativeEvent.offsetY');
    }

    /**
     * @member {external:Number} pageX
     * @label property
     * @syntax
     * get pageX(): Number
     * @summary Gets the distance in pixels between the mouse pointer and the
     *  left edge of the page.
     * @description The page refers to the entire web page, even parts that are
     *  not currently viewable in the browser. Therefore, two clicks on the same
     *  physical point of the screen return different `pageX` values if the
     *  page is scrolled horizontally in the meantime. But two clicks on the
     *  same point on the page return the same `pageX` values even if the
     *  page is scrolled horizontally in the meantime.
     *  ![Coodinate Reference](images/coordinates_page.png "Coordinate Reference")
     * @snippet [onClick-get_coordinates.es6=Get a mouse click's coordinates]
     * @memberof $w.MouseEvent
     * @readonly
     */

    /**
     * @member {external:Number} pageY
     * @label property
     * @syntax
     * get pageY(): Number
     * @summary Gets the distance in pixels between the mouse pointer and the
     *  top edge of the page.
     * @description The page refers to the entire web page, even parts that are
     *  not currently viewable in the browser. Therefore, two clicks on the same
     *  physical point of the screen return different `pageY` values if the
     *  page is scrolled vertically in the meantime. But two clicks on the
     *  same point on the page return the same `pageY` values even if the
     *  page is scrolled vertically in the meantime.
     *  ![Coodinate Reference](images/coordinates_page.png "Coordinate Reference")
     * @snippet [onClick-get_coordinates.es6=Get a mouse click's coordinates]
     * @memberof $w.MouseEvent
     * @readonly
     */


    /**
     * @member {external:Number} screenX
     * @label property
     * @syntax
     * get screenX(): Number
     * @summary Gets the distance in pixels between the mouse pointer and the
     *  left edge of the screen or screens.
     * @description The screen refers to the physical screen or screens.
     *  Therefore, two clicks on the same physical point of the screen
     *  return the same `screenX` values even if the page is scrolled horizontally in
     *  the meantime. But two clicks on the
     *  same point on the page return different `screenX` values if the
     *  page is scrolled horizontally in the meantime.
     *  ![Coodinate Reference](images/coordinates_screen.png "Coordinate Reference")
     * @snippet [onClick-get_coordinates.es6=Get a mouse click's coordinates]
     * @memberof $w.MouseEvent
     * @readonly
     */


    /**
     * @member {external:Number} screenY
     * @label property
     * @syntax
     * get screenY(): Number
     * @summary Gets the distance in pixels between the mouse pointer and the
     *  top edge of the screen or screens.
     * @description The screen refers to the physical screen or screens.
     *  Therefore, two clicks on the same physical point of the screen
     *  return the same `screenY` values if the page is scrolled vertically in
     *  the meantime. But two clicks on the
     *  same point on the page return different `screenY` values if the
     *  page is scrolled vertically in the meantime.
     *  ![Coodinate Reference](images/coordinates_screen.png "Coordinate Reference")
     * @snippet [onClick-get_coordinates.es6=Get a mouse click's coordinates]
     * @memberof $w.MouseEvent
     * @readonly
     */


    /**
     * @member {external:Number} clientX
     * @label property
     * @syntax
     * get clientX(): Number
     * @summary Gets the distance in pixels between the mouse pointer and the
     *  left edge of the browser's viewable area.
     * @description The browser's viewable area refers to the viewable contents
     *  of the page and excludes elements such as a sidebar and scrollbar.
     *  Therefore, two clicks on the same physical point of the screen
     *  return the same `clientX` values even if the page is scrolled horizontally in
     *  the meantime. But two clicks on the
     *  same point on the page return different `clientX` values if the
     *  page is scrolled horizontally in the meantime.
     *  ![Coodinate Reference](images/coordinates_client.png "Coordinate Reference")
     * @snippet [onClick-get_coordinates.es6=Get a mouse click's coordinates]
     * @memberof $w.MouseEvent
     * @readonly
     */


    /**
     * @member {external:Number} clientY
     * @label property
     * @syntax
     * get clientY(): Number
     * @summary Gets the distance in pixels between the mouse pointer and the
     *  top edge of the browser's viewable area.
     * @description The browser's viewable area refers to the viewable contents
     *  of the page and excludes elements such as a toolbar and scrollbar.
     *  Therefore, two clicks on the same physical point of the screen
     *  return the same `clientY` values if the page is scrolled vertically in
     *  the meantime. But two clicks on the
     *  same point on the page return different `clientY` values if the
     *  page is scrolled vertically in the meantime.
     *  ![Coodinate Reference](images/coordinates_client.png "Coordinate Reference")
     * @snippet [onClick-get_coordinates.es6=Get a mouse click's coordinates]
     * @memberof $w.MouseEvent
     * @readonly
     */


    /**
     * @member {external:Number} offsetX
     * @label property
     * @syntax
     * get offsetX(): Number
     * @summary Gets the distance in pixels between the mouse pointer and the
     *  left edge of the element that triggered this event.
     * @description
     *  Scrolling the web page causes its elements to shift relative to the
     *  browser window. Therefore, two clicks on the same physical point of the
     *  screen return different `offsetX` values if the page is scrolled
     *  horizontally in the meantime. But two clicks on the
     *  same point on the element return the same `offsetX` values even if the
     *  page is scrolled horizontally in the meantime.
     * @snippet [onClick-get_coordinates.es6=Get a mouse click's coordinates]
     * @memberof $w.MouseEvent
     * @readonly
     */


    /**
     * @member {external:Number} offsetY
     * @label property
     * @syntax
     * get offsetY(): Number
     * @summary Gets the distance in pixels between the mouse pointer and the
     *  top edge of the element that triggered this event.
     * @description
     *  Scrolling the web page causes its elements to shift relative to the
     *  browser window. Therefore, two clicks on the same physical point of the
     *  screen return different `offsetY` values if the page is scrolled
     *  vertically in the meantime. But two clicks on the
     *  same point on the element return the same `offsetY` values even if the
     *  page is scrolled horizontally in the meantime.
     * @snippet [onClick-get_coordinates.es6=Get a mouse click's coordinates]
     * @memberof $w.MouseEvent
     * @readonly
     */

}

export default MouseEvent;
